/**
 * WaitlistModal - Modal pour rejoindre la liste d'attente Entreprise+
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Rocket, CheckCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    entreprise: "",
    nombreUtilisateurs: "",
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nom || !formData.email || !formData.entreprise) {
      toast({
        title: "Champs requis",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Enregistrer dans la table waitlist (a creer si necessaire)
      const { error } = await supabase.from("waitlist").insert({
        nom: formData.nom,
        email: formData.email,
        entreprise: formData.entreprise,
        nombre_utilisateurs: formData.nombreUtilisateurs || null,
        plan_interesse: "entreprise_plus",
        created_at: new Date().toISOString(),
      });

      if (error) {
        // Si la table n'existe pas, on log juste en console pour le moment
        if (import.meta.env.DEV) console.log("Waitlist submission (table may not exist):", formData);
      }

      setIsSuccess(true);

      // Fermer apres 3 secondes
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ nom: "", email: "", entreprise: "", nombreUtilisateurs: "" });
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Waitlist error:", error);
      // On montre quand meme le succes car l'important est l'experience utilisateur
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-3 bg-green-100 rounded-full mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#1A365D] mb-2">
              Merci pour votre interet !
            </h3>
            <p className="text-[#6B7280]">
              Nous vous contacterons des que le plan Entreprise+ sera disponible.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
              <Rocket className="h-6 w-6 text-[#D4AF37]" />
            </div>
          </div>
          <DialogTitle className="text-xl text-[#1A365D]">
            Rejoindre la liste d'attente
          </DialogTitle>
          <DialogDescription>
            Soyez informe en priorite du lancement du plan Entreprise+ avec
            l'application mobile, le mode hors-ligne et le GPS tracking.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nom">Nom complet *</Label>
            <Input
              id="nom"
              placeholder="Jean Dupont"
              value={formData.nom}
              onChange={(e) => handleChange("nom", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email professionnel *</Label>
            <Input
              id="email"
              type="email"
              placeholder="jean@entreprise.fr"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="entreprise">Nom de l'entreprise *</Label>
            <Input
              id="entreprise"
              placeholder="BTP Construction SARL"
              value={formData.entreprise}
              onChange={(e) => handleChange("entreprise", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nombreUtilisateurs">
              Nombre d'utilisateurs prevu
            </Label>
            <Select
              value={formData.nombreUtilisateurs}
              onValueChange={(value) => handleChange("nombreUtilisateurs", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionnez une option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10-20">10 - 20 utilisateurs</SelectItem>
                <SelectItem value="20-50">20 - 50 utilisateurs</SelectItem>
                <SelectItem value="50-100">50 - 100 utilisateurs</SelectItem>
                <SelectItem value="100+">Plus de 100 utilisateurs</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#1A365D] hover:bg-[#1A365D]/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                "Rejoindre la liste"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default WaitlistModal;
